// Lightweight markdown editor for forum posts & replies.
//
// Wholesalers aren't developers — so instead of asking them to learn
// markdown syntax cold, we ship a toolbar that wraps the selection with
// the right glyphs and a "Preview" tab that renders the live result.
// Storage format is plain markdown (cheap, portable, no vendor lock-in).
//
// Toolbar covers the 90% case: bold, italic, link, bullet list, code block.
// We intentionally don't add headings or tables — keeps posts focused.
import { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bold, Italic, Link2, List, Code, Eye, Edit3 } from "lucide-react";

function wrapSelection(textarea, before, after = before) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = textarea.value;
  const selected = value.slice(start, end);
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  return { next, selStart: start + before.length, selEnd: end + before.length };
}

const TOOLBAR = [
  { id: "bold",   icon: Bold,   wrap: ["**", "**"], title: "Bold" },
  { id: "italic", icon: Italic, wrap: ["_", "_"],   title: "Italic" },
  { id: "list",   icon: List,   wrap: ["\n- ", ""], title: "Bullet list" },
  { id: "code",   icon: Code,   wrap: ["`", "`"],   title: "Inline code" },
];

export default function MarkdownEditor({
  value,
  onChange,
  placeholder,
  rows = 6,
  testIdPrefix = "md-editor",
}) {
  const [mode, setMode] = useState("write");
  const ref = useRef(null);

  const apply = (before, after) => {
    const ta = ref.current;
    if (!ta) return;
    const res = wrapSelection(ta, before, after);
    onChange(res.next);
    // Restore selection on next frame.
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(res.selStart, res.selEnd);
    });
  };

  const insertLink = () => {
    const url = window.prompt("Link URL");
    if (!url) return;
    apply("[", `](${url})`);
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-1 px-2 py-1 border-b bg-zinc-50">
        {TOOLBAR.map((b) => (
          <button
            key={b.id}
            type="button"
            onClick={() => apply(b.wrap[0], b.wrap[1])}
            title={b.title}
            className="p-1.5 rounded hover:bg-white text-zinc-600"
            data-testid={`${testIdPrefix}-toolbar-${b.id}`}
          >
            <b.icon size={15} />
          </button>
        ))}
        <button
          type="button"
          onClick={insertLink}
          title="Link"
          className="p-1.5 rounded hover:bg-white text-zinc-600"
          data-testid={`${testIdPrefix}-toolbar-link`}
        >
          <Link2 size={15} />
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setMode("write")}
          className={
            "px-2 py-1 text-xs rounded inline-flex items-center gap-1 " +
            (mode === "write" ? "bg-white shadow-sm text-zinc-900 font-medium" : "text-zinc-600")
          }
          data-testid={`${testIdPrefix}-tab-write`}
        >
          <Edit3 size={12} /> Write
        </button>
        <button
          type="button"
          onClick={() => setMode("preview")}
          className={
            "px-2 py-1 text-xs rounded inline-flex items-center gap-1 " +
            (mode === "preview" ? "bg-white shadow-sm text-zinc-900 font-medium" : "text-zinc-600")
          }
          data-testid={`${testIdPrefix}-tab-preview`}
        >
          <Eye size={12} /> Preview
        </button>
      </div>
      {mode === "write" ? (
        <textarea
          ref={ref}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-sm focus:outline-none resize-y"
          data-testid={`${testIdPrefix}-textarea`}
        />
      ) : (
        <div
          className="prose prose-sm max-w-none px-3 py-3 min-h-[120px]"
          data-testid={`${testIdPrefix}-preview`}
        >
          {value.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          ) : (
            <p className="text-zinc-400 italic">Nothing to preview yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
